/**
 * i18n configuration for bilingual EN ↔ ES experience.
 *
 * Strategy:
 *  - Store the user's language preference in cookie `wts_lang` + localStorage fallback.
 *  - `/es` and `/es/:slug` native routes lock language to `es` regardless of preference
 *    (handled in App-level listener). The toggle still flips preference for the rest
 *    of the site.
 *  - Translations live under `src/i18n/locales/{lang}/{namespace}.json`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enFooter from './locales/en/footer.json';
import enHome from './locales/en/home.json';
import enDashboard from './locales/en/dashboard.json';

import esCommon from './locales/es/common.json';
import esNav from './locales/es/nav.json';
import esFooter from './locales/es/footer.json';
import esHome from './locales/es/home.json';
import esDashboard from './locales/es/dashboard.json';

export const SUPPORTED_LANGUAGES = ['en', 'es'];
export const DEFAULT_LANGUAGE = 'en';
export const LANG_COOKIE = 'wts_lang';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon, nav: enNav, footer: enFooter, home: enHome, dashboard: enDashboard },
      es: { common: esCommon, nav: esNav, footer: esFooter, home: esHome, dashboard: esDashboard },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: ['common', 'nav', 'footer', 'home', 'dashboard'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['cookie', 'localStorage', 'navigator'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: LANG_COOKIE,
      lookupLocalStorage: LANG_COOKIE,
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    react: { useSuspense: false },
  });

// Keep <html lang="..."> in sync for accessibility + SEO.
const syncHtmlLang = (lng) => {
  try { document.documentElement.lang = (lng || DEFAULT_LANGUAGE).split('-')[0]; } catch (e) { /* noop */ }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
